/**
 * useSourceSystems — the source-system registry, fetched once (#3385).
 *
 * ``GET /v1/twin/provenance/source-systems`` serves
 * ``config/source-systems.yaml``: deploy-wide config naming the external
 * systems a twin data point can be attributed to. It carries no tenant data
 * and cannot change while the backend image is running (the loader is
 * ``@lru_cache``'d and the file is baked into the image), so it is fetched
 * ONCE per session — ``staleTime: Infinity`` — and shared by every object
 * view through one query key.
 *
 * ⚠️ **A registry outage must never blank the badges.** While the fetch is in
 * flight, and if it fails outright, this returns ``DEFAULT_SOURCE_SYSTEMS``:
 * the five-entry table the grid used to freeze into the bundle. Degrading to
 * an empty list would render every data point unattributed — indistinguishable
 * from "this farm has no attributed data", which is precisely the silent
 * failure #3385 exists to remove.
 *
 * ⚠️ This hook is deliberately NOT called inside ``PropertyGrid``. The grid is
 * rendered bare (no ``QueryClientProvider``) by
 * ``PropertyGridProvenance.test.tsx`` and reachable from surfaces that mount no
 * provider, so the registry arrives there as a prop with a module-level
 * default. The composition roots that call this are ``ObjectView`` (for the
 * grid's badges) and ``FarmPage`` (for the feed's alias note) — both, or the
 * two surfaces attribute the same alias differently.
 */
import { useQuery } from '@tanstack/react-query';

import { twinService, type SourceSystemEntry } from '../services/twinService';
import { DEFAULT_SOURCE_SYSTEMS } from '../pages/farm/object/provenanceSource';

/** One key for the whole app: the registry is deploy-wide, not per entity. */
export const sourceSystemKeys = {
  all: ['twin', 'provenance', 'source-systems'] as const,
};

/**
 * The registry, or the bundled defaults while loading / on error.
 *
 * Returns the array itself rather than the query result: every caller wants
 * "a table I can resolve tokens against", and the reference is stable
 * (React Query's cached array, or the frozen module constant), which is what
 * lets ``resolveProvenanceSource`` cache its compiled matchers by identity.
 */
export function useSourceSystems(): ReadonlyArray<SourceSystemEntry> {
  const { data } = useQuery({
    queryKey: sourceSystemKeys.all,
    queryFn: () => twinService.getSourceSystems(),
    staleTime: Infinity,
    gcTime: Infinity,
    // Config, not a live read: no refetch churn on tab focus or reconnect.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  // EMPTY counts as "no usable answer", not as an answer. The backend refuses
  // to serve `{"source_systems": []}` for exactly this reason (it 500s
  // instead), so an empty array can only arrive from a shape mismatch or a
  // future regression — and with staleTime/gcTime Infinity and no refetch, it
  // would blank every badge for the WHOLE session with no way back.
  return data && data.length > 0 ? data : DEFAULT_SOURCE_SYSTEMS;
}

export default useSourceSystems;
